import { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { PageHeader, Btn, Input, Field } from "@/shared/components/UI";
import { Plus, X, ShieldCheck, Pencil, Trash2, Users as UsersIcon } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = {
  management: "Management", scope_user: "Scope User", csm: "CSM", psm: "PSM",
  ism: "ISM", site_inspector: "Site Inspector", designer: "Designer",
  prepress: "Pre-Press", production: "Production", procurement_user: "Procurement",
  pre_audit: "Pre-Audit", accounts: "Accounts", sales: "Sales",
};

export default function UserTypes() {
  const [types, setTypes] = useState([]);
  const [registry, setRegistry] = useState(null); // {pages, base_roles, archetype_pages}
  const [editing, setEditing] = useState(null);    // type object or {} for new

  function load() {
    api.get("/user-types").then((r) => setTypes(r.data)).catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
    api.get("/page-registry").then((r) => setRegistry(r.data)).catch(() => {});
  }
  useEffect(load, []);

  async function del(t) {
    try {
      await api.delete(`/user-types/${t.id}`);
      toast.success(`Deleted "${t.name}"`);
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  const pageLabel = useMemo(() => {
    const m = {};
    (registry?.pages || []).forEach((p) => { m[p.key] = p.label; });
    return m;
  }, [registry]);

  return (
    <div className="p-6 lg:p-10" data-testid="user-types-page">
      <PageHeader kicker="Admin" title="User Types">
        <Btn onClick={() => setEditing({})} data-testid="add-user-type-btn">
          <Plus className="w-4 h-4" /> New User Type
        </Btn>
      </PageHeader>

      <p className="text-sm text-zinc-500 mb-5 max-w-2xl">
        Create a named access template built on a base role (e.g. a "Junior CSM" that behaves like a CSM
        but can only open a few pages). Pick the base role, then tick the pages this type may access.
        These types appear in the role picker when creating a user.
      </p>

      {types.length === 0 ? (
        <div className="card-flat p-12 text-center text-zinc-400" data-testid="user-types-empty">
          <ShieldCheck className="w-8 h-8 mx-auto mb-3 text-zinc-300" />
          No custom user types yet. Create one to give a tailored access set.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {types.map((t) => (
            <div key={t.id} className="card-flat p-4 flex flex-col" data-testid={`user-type-card-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-base truncate">{t.name}</div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-orange-600 mt-0.5">
                    base: {ROLE_LABEL[t.base_role] || t.base_role}
                  </div>
                </div>
                <span className="inline-flex items-center gap-1 text-[11px] text-zinc-500 shrink-0">
                  <UsersIcon className="w-3.5 h-3.5" /> {t.user_count}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-3 flex-1">
                {(t.allowed_pages || []).map((k) => (
                  <span key={k} className="px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-700 text-[10px] font-mono">
                    {pageLabel[k] || k}
                  </span>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing(t)} data-testid={`edit-user-type-${t.id}`}
                  className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-orange-600 text-white text-[11px] font-medium hover:bg-orange-700">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => del(t)} data-testid={`delete-user-type-${t.id}`}
                  className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full border border-rose-300 text-rose-700 text-[11px] font-medium hover:bg-rose-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && registry && (
        <UserTypeDialog
          type={editing.id ? editing : null}
          registry={registry}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function UserTypeDialog({ type, registry, onClose, onSaved }) {
  const [name, setName] = useState(type?.name || "");
  const [baseRole, setBaseRole] = useState(type?.base_role || registry.base_roles[0]);
  const [pages, setPages] = useState(new Set(type?.allowed_pages || []));
  const [busy, setBusy] = useState(false);

  const archetypePages = registry.archetype_pages[baseRole] || [];

  // When base role changes, drop any selected pages no longer valid.
  useEffect(() => {
    setPages((prev) => new Set([...prev].filter((p) => archetypePages.includes(p))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRole]);

  const pageMeta = useMemo(() => {
    const m = {};
    registry.pages.forEach((p) => { m[p.key] = p; });
    return m;
  }, [registry]);

  function toggle(k) {
    setPages((prev) => {
      const n = new Set(prev);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });
  }

  async function save(e) {
    e.preventDefault();
    if (busy) return;
    if (!name.trim()) { toast.error("Enter a name"); return; }
    if (pages.size === 0) { toast.error("Select at least one page"); return; }
    setBusy(true);
    try {
      const payload = { name: name.trim(), base_role: baseRole, allowed_pages: [...pages] };
      if (type) await api.patch(`/user-types/${type.id}`, payload);
      else await api.post("/user-types", payload);
      toast.success(type ? "User type updated" : "User type created");
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={save} data-testid="user-type-form"
        className="bg-white max-w-lg w-full max-h-[90vh] flex flex-col rounded-2xl border border-zinc-200 overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-between bg-zinc-50 border-b border-zinc-200 px-5 py-3">
          <h3 className="font-display font-bold text-lg">{type ? "Edit user type" : "New user type"}</h3>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <Field label="Type name" required>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Junior CSM"
              required data-testid="user-type-name" />
          </Field>
          <Field label="Base role (defines workflow powers)" required>
            <select value={baseRole} onChange={(e) => setBaseRole(e.target.value)}
              className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm" data-testid="user-type-base-role">
              {registry.base_roles.map((r) => (
                <option key={r} value={r}>{ROLE_LABEL[r] || r}</option>
              ))}
            </select>
          </Field>
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                Accessible pages ({pages.size}/{archetypePages.length})
              </label>
              <button type="button" onClick={() => setPages(new Set(archetypePages))}
                className="text-[11px] font-mono uppercase tracking-wider text-orange-700 hover:underline"
                data-testid="user-type-select-all">select all</button>
            </div>
            <div className="grid grid-cols-2 gap-1.5 max-h-64 overflow-y-auto pr-1" data-testid="user-type-pages">
              {archetypePages.map((k) => {
                const on = pages.has(k);
                return (
                  <button type="button" key={k} onClick={() => toggle(k)}
                    data-testid={`user-type-page-${k.replace(/\//g, "_")}`}
                    className={`text-left rounded-lg border px-2.5 py-2 text-xs transition ${
                      on ? "ring-2 ring-orange-500 border-orange-300 bg-orange-50/50 text-zinc-900"
                         : "border-zinc-200 hover:border-zinc-300 text-zinc-600"}`}>
                    {pageMeta[k]?.label || k}
                  </button>
                );
              })}
            </div>
            <p className="text-[11px] text-zinc-400 mt-2">Only pages this base role can normally access are shown.</p>
          </div>
        </div>
        <div className="flex-shrink-0 flex gap-2 px-5 py-3 bg-zinc-50 border-t border-zinc-200">
          <Btn type="submit" disabled={busy} data-testid="save-user-type">
            {busy ? "Saving…" : type ? "Save changes" : "Create user type"}
          </Btn>
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
        </div>
      </form>
    </div>
  );
}
